import React from 'react'
import { isFunction } from 'lodash'
import pluralize from 'pluralize'

import { Pill } from '@/components/pills/Pill'

import { FilterPillAction } from './FilterPillAction'

import './FilterPill.scss'

/**
 * @import import { FilterPill } from '@/components/filters/FilterPill'
 * @param {object} props
 * @param {string} [props.customPlaceholder] Bold text shown instead of the label while there is no value
 * @param {string} [props.accessor] Filter identifier passed to format
 * @param {string} [props.label] Filter name shown in bold
 * @param {(event: MouseEvent) => void} [props.handleRemoveFilter] Called when the clear icon is clicked
 * @param {() => void} [props.handleOpenFilterModal] Called when the pill is clicked
 * @param {(accessor: string) => string} [props.format] Derives the display value; takes precedence over value
 * @param {string} [props.value] Current filter value shown after the label
 * @param {boolean} [props.isClearable] Swaps the filter icon for a clear icon on hover while there is a value
 * @param {boolean} [props.loading] Blocks clearing while a filter request is in flight
 * @param {boolean} [props.showAllOnEmpty] Shows an "All" value instead of the ghosted empty state
 * @param {string} [props.showAllOnEmptyLabel] Custom text for the empty "All" value
 * @param {React.ReactNode} [props.icon] Replaces the filter action icon entirely
 * @param {React.ReactNode} [props.customFilterIcon] Replaces the default filter icon inside the action
 * @param {boolean} [props.showLabel] Hides the label when false
 * @param {string} [props.dataTestId] data-testid on the pill
 * @param {boolean} [props.error] Critical styling on the pill
 * @param {boolean} [props.active] Active styling, used while the filter popup is open
 */
export const FilterPill = ({
  customPlaceholder,
  accessor,
  label,
  handleRemoveFilter,
  handleOpenFilterModal,
  format,
  value = '',
  isClearable = true,
  loading,
  showAllOnEmpty = false,
  showAllOnEmptyLabel = null,
  icon,
  customFilterIcon,
  showLabel = true,
  dataTestId,
  error,
  active = false,
  ...props
}) => {
  const displayValue = isFunction(format) ? format(accessor) : value
  const hasSelection = !!displayValue
  const isGhosted = !hasSelection && !showAllOnEmpty

  return (
    <Pill
      dataTestId={dataTestId}
      className="filter-pill-button"
      transparent
      ghost={isGhosted}
      error={error}
      active={active}
      onClick={() => {
        if (handleOpenFilterModal) {
          handleOpenFilterModal()
        }
      }}
      {...props}
    >
      {icon || (
        <FilterPillAction
          customFilterIcon={customFilterIcon}
          showAllOnEmpty={showAllOnEmpty}
          hasSelection={hasSelection}
          handleRemoveFilter={handleRemoveFilter}
          loading={loading}
          isClearable={isClearable}
        />
      )}
      {customPlaceholder && !hasSelection ? (
        <strong>{customPlaceholder}</strong>
      ) : (
        showLabel && (
          <strong>
            {label}
            {(hasSelection || showAllOnEmpty) && ':'}
          </strong>
        )
      )}
      {showAllOnEmpty && !hasSelection
        ? showAllOnEmptyLabel || `All ${pluralize(label)}`
        : hasSelection && (
            <span className="filter-pill-value" data-testid="filter-pill-value">
              {displayValue}
            </span>
          )}
    </Pill>
  )
}
